import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Building2, Phone, Mail, FileSpreadsheet } from 'lucide-react';
import { proveedoresService } from '@/services/proveedores.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import { ImportSuppliersModal } from './import-suppliers-modal';

export function SuppliersListPage() {
  const [search, setSearch] = useState('');
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { handleAuthRedirect } = useApiError();

  useEffect(() => {
    loadSuppliers();
  }, [search]);

  const loadSuppliers = async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await proveedoresService.findAll(user.company.id, search || undefined);
      setSuppliers(data || []);
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Proveedores</h1>
          <p className="text-sm text-ios-gray mt-1">Gestiona tus proveedores</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-3 py-2 rounded-xl text-sm font-medium border border-gray-300 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
          >
            <FileSpreadsheet size={16} className="text-green-600" />
            Importar Excel
          </button>
          <button onClick={() => navigate('/suppliers/new')} className="ios-button flex items-center gap-2">
            <Plus size={18} />
            Nuevo Proveedor
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ios-gray" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar proveedor por nombre, RFC o contacto..."
          className="ios-input w-full pl-10"
        />
      </div>

      {error && (
        <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100 mb-4">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin" />
        </div>
      ) : suppliers.length === 0 ? (
        <div className="ios-card p-12 text-center">
          <Building2 size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <h3 className="text-lg font-medium text-ios-gray mb-1">No hay proveedores</h3>
          <p className="text-sm text-ios-gray mb-4">
            {search ? 'No se encontraron proveedores' : 'Agrega tu primer proveedor'}
          </p>
          {!search && (
            <button onClick={() => navigate('/suppliers/new')} className="ios-button">
              + Nuevo Proveedor
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {suppliers.map((supplier: any) => (
            <div
              key={supplier.id}
              onClick={() => navigate(`/suppliers/${supplier.id}`)}
              className="ios-card p-4 hover:shadow-ios-md transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-ios-green/10 rounded-[10px] flex items-center justify-center flex-shrink-0">
                    <Building2 size={20} className="text-ios-green" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{supplier.name}</p>
                    {supplier.contactName && (
                      <p className="text-xs text-ios-gray mt-0.5">{supplier.contactName}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1.5">
                      {supplier.phone && (
                        <span className="text-[11px] text-ios-gray flex items-center gap-1">
                          <Phone size={11} /> {supplier.phone}
                        </span>
                      )}
                      {supplier.email && (
                        <span className="text-[11px] text-ios-gray flex items-center gap-1">
                          <Mail size={11} /> {supplier.email}
                        </span>
                      )}
                      {supplier.rfc && (
                        <code className="text-[10px] bg-ios-gray-6 px-1.5 py-0.5 rounded font-mono">{supplier.rfc}</code>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {supplier.paymentTerms && (
                    <span className="text-[10px] bg-ios-blue/10 text-ios-blue px-2 py-1 rounded-full font-medium">
                      {supplier.paymentTerms === 'CONTADO' ? 'Contado' : `${supplier.paymentTerms} días`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showImportModal && (
        <ImportSuppliersModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            loadSuppliers();
          }}
        />
      )}
    </div>
  );
}

